import { useId, useState } from 'react';

import Button from './Button';
import './Quote.css';

interface QuoteProps {
  /** Verbatim document text. Whitespace and line breaks are preserved. */
  children: string;
  /** Evidence strength of the flag this quote supports. */
  tone?: 'blue' | 'amber' | 'neutral';
  /** Section reference, page, or table id the quote was taken from. */
  cite?: string;
  /**
   * Collapse to this many lines behind an explicit control. Omit — the default
   * — and the quote is never truncated.
   */
  clampLines?: number;
  className?: string;
}

/**
 * Not a Disclosure, deliberately. A clamped quote is truncated, not hidden:
 * the text is on the page and the control lengthens it. Disclosure is for a
 * section that is absent until it is opened. The two read differently and
 * behave differently, and collapsing them would make one of them wrong.
 */
export default function Quote({
  children,
  tone = 'blue',
  cite,
  clampLines,
  className,
}: QuoteProps) {
  const [expanded, setExpanded] = useState(false);
  const textId = useId();

  const clamped = clampLines !== undefined && !expanded;

  const classes = [
    'quote',
    tone === 'blue' ? null : `quote--${tone}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <figure className={classes}>
      <blockquote
        id={textId}
        className={`quote__text${clamped ? ' quote__text--clamped' : ''}`}
        style={
          clampLines === undefined
            ? undefined
            : ({ '--quote-lines': clampLines } as React.CSSProperties)
        }
      >
        {children}
      </blockquote>

      {cite || clampLines !== undefined ? (
        <figcaption className="quote__foot">
          {cite ? <span className="quote__cite">{cite}</span> : <span />}
          {clampLines !== undefined ? (
            <Button
              variant="ghost"
              aria-expanded={expanded}
              aria-controls={textId}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Collapse quote' : 'Show full quote'}
            </Button>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}
